import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Col, Form, Modal, Row,
} from 'react-bootstrap';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import ErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/ErrorWrapper/ErrorWrapper';
import FormAlertBox
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormAlertBox/FormAlertBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleRight } from '@fortawesome/pro-solid-svg-icons';
// import { useHistory } from 'react-router-dom';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';

// Update this to match the route mapped to doLoginWithSubscription().
const LOGIN_ENDPOINT = '/app/gateway/login-with-subscription';

const LOGIN_FIELD_LIST = ['email', 'password', 'subscriptionId'];
const ACCESS_LINK_FIELD_LIST = ['email'];
const AUTO_LOGIN_FIELD_LIST = ['accessLink'];

const UNEXPECTED_ERROR = { message: 'Unexpected error while trying to log in.' };
const ACCESS_LINK_UNEXPECTED_ERROR = {
  field: 'email',
  message: 'Unexpected error while requesting an access link.',
};

const getResponseData = (response) => {
  if (!response) return null;
  return response.data || response;
};

const extractServerErrors = (responseData) => {
  if (!responseData) return [];
  if (responseData.success === false) {
    const list = responseData.data || responseData.content || responseData.errors || [];
    return Array.isArray(list) ? list : [];
  }
  const list = responseData.content || responseData.errors || [];
  return Array.isArray(list) ? list : [];
};

const extractAccessLinkErrors = (responseData) => {
  if (!responseData) return [];

  if (responseData.success === false) {
    const errorMessage = responseData.data && responseData.data.error;

    if (errorMessage) {
      return [{ field: 'email', message: errorMessage }];
    }

    const serverErrors = extractServerErrors(responseData);
    return serverErrors.map((error) => ({
      ...error,
      field: error.field || 'email',
    }));
  }

  return [];
};

const FreeTrialLoginForm = ({ subscriptionId, initialEmail, autoLoginErrors }) => {
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Access-link modal state
  const [showAccessLinkModal, setShowAccessLinkModal] = useState(false);
  const [accessLinkEmail, setAccessLinkEmail] = useState('');
  const [accessLinkErrors, setAccessLinkErrors] = useState([]);
  const [accessLinkSubmitting, setAccessLinkSubmitting] = useState(false);
  const [accessLinkRequested, setAccessLinkRequested] = useState(false);

  // Re-target any backend error so ErrorWrapper on the access-link button picks it up.
  const autoLoginErrorsForButton = (autoLoginErrors || []).map((err) => ({
    ...err,
    field: 'accessLink',
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('emailAddress', email);
      formData.append('password', password);
      formData.append('subscriptionId', Number(subscriptionId));

      const response = await axios.post(
        LOGIN_ENDPOINT,
        formData,
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          validateStatus: (status) => status >= 200 && status < 400,
        },
      );

      const serverErrors = extractServerErrors(response.data);
      if (serverErrors.length > 0 || (response.data && response.data.success === false)) {
        setErrors(serverErrors.length > 0 ? serverErrors : [UNEXPECTED_ERROR]);
        return;
      }

      window.location.replace('/s/welcome');
    } catch (err) {
      const serverErrors = extractServerErrors(err.response && err.response.data);
      setErrors(serverErrors.length > 0 ? serverErrors : [UNEXPECTED_ERROR]);
    } finally {
      setSubmitting(false);
    }
  };

  const openAccessLinkModal = () => {
    setAccessLinkEmail(email);
    setAccessLinkErrors([]);
    setAccessLinkRequested(false);
    setShowAccessLinkModal(true);
  };

  const closeAccessLinkModal = () => {
    setShowAccessLinkModal(false);
  };

  const handleSendAccessLink = async (e) => {
    e.preventDefault();
    setAccessLinkErrors([]);
    setAccessLinkSubmitting(true);

    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance().post(
        `${envConfig.apiUrl}/api/v1/user/one-time-email`,
        {
          data: {
            emailAddress: accessLinkEmail.trim(),
            subscriptionId: Number(subscriptionId),
          },
        },
      );

      const responseData = getResponseData(response);
      const accessLinkServerErrors = extractAccessLinkErrors(responseData);

      if (accessLinkServerErrors.length > 0 || (responseData && responseData.success === false)) {
        setAccessLinkErrors(
          accessLinkServerErrors.length > 0
            ? accessLinkServerErrors
            : [ACCESS_LINK_UNEXPECTED_ERROR],
        );
        return;
      }

      setAccessLinkRequested(true);
    } catch (err) {
      const responseData = getResponseData(err.response);
      const accessLinkServerErrors = extractAccessLinkErrors(responseData);

      setAccessLinkErrors(
        accessLinkServerErrors.length > 0
          ? accessLinkServerErrors
          : [ACCESS_LINK_UNEXPECTED_ERROR],
      );
    } finally {
      setAccessLinkSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="h3 mb-4 ml-2">Sign In To Start Your Free Trial</h2>

      <div className="d-flex align-items-center mb-3 ml-3">
        <FontAwesomeIcon icon={faArrowCircleRight} className="text-danger mr-2" size="lg" />
        <span className="h5 mb-0">Log In</span>
      </div>

      <FormErrorHandler
        errorList={errors}
        fieldList={LOGIN_FIELD_LIST}
        isFormDisabled={submitting}
      >
        <FormAlertBox />

        <Form onSubmit={handleSubmit} noValidate className="mb-4">
          <Form.Group as={Row} controlId="ft-email" className="mb-2">
            <Form.Label column xs="auto" className="ml-5 pl-2" style={{ width: '6rem' }}>Email</Form.Label>
            <Col className="pl-0">
              <ErrorWrapper fieldId="email">
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                  style={{ maxWidth: '18rem' }}
                />
              </ErrorWrapper>
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="ft-password" className="mb-3">
            <Form.Label column xs="auto" className="ml-5 pl-2" style={{ width: '6rem' }}>Password</Form.Label>
            <Col className="pl-0">
              <ErrorWrapper fieldId="password">
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  style={{ maxWidth: '18rem' }}
                />
              </ErrorWrapper>
            </Col>
          </Form.Group>

          <Row>
            <Col xs="auto" className="ml-5 pl-2" style={{ width: '6rem' }} aria-hidden="true" />
            <Col className="pl-0">
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Logging in…' : 'Login'}
              </Button>
            </Col>
          </Row>
        </Form>
      </FormErrorHandler>

      <div className="d-flex align-items-center mb-3 ml-3 mt-5">
        <FontAwesomeIcon icon={faArrowCircleRight} className="text-danger mr-2" size="lg" />
        <span className="h5 mb-0">Forgot Your Password?</span>
      </div>

      <FormErrorHandler
        errorList={autoLoginErrorsForButton}
        fieldList={AUTO_LOGIN_FIELD_LIST}
        isFormDisabled={false}
      >
        <div className="ml-5 pl-2">
          <ErrorWrapper fieldId="accessLink">
            <Button type="button" variant="primary" onClick={openAccessLinkModal}>
              Request Access Link
            </Button>
          </ErrorWrapper>
        </div>
      </FormErrorHandler>

      <Modal
        show={showAccessLinkModal}
        onHide={closeAccessLinkModal}
        centered
        aria-labelledby="access-link-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="access-link-modal-title" className="text-center w-100">
            Request An Access Link
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {accessLinkRequested ? (
            <Alert variant="success" className="mb-0">
              An access link has been sent to your email address.
            </Alert>
          ) : (
            <FormErrorHandler
              errorList={accessLinkErrors}
              fieldList={ACCESS_LINK_FIELD_LIST}
              isFormDisabled={accessLinkSubmitting}
            >
              <FormAlertBox />
              <p className="text-center">
                Enter your email address to receive a one-time access link
              </p>
              <Form onSubmit={handleSendAccessLink} noValidate>
                <Form.Group as={Row} controlId="access-link-email" className="mb-3 justify-content-center">
                  <Form.Label column xs="auto">Email</Form.Label>
                  <Col xs="auto">
                    <ErrorWrapper fieldId="email">
                      <Form.Control
                        type="email"
                        value={accessLinkEmail}
                        onChange={(e) => setAccessLinkEmail(e.target.value)}
                        autoComplete="username"
                        required
                        style={{ width: '18rem' }}
                      />
                    </ErrorWrapper>
                  </Col>
                </Form.Group>
                <div className="text-center">
                  <Button type="submit" variant="primary" disabled={accessLinkSubmitting}>
                    {accessLinkSubmitting ? 'Sending…' : 'Send Link'}
                  </Button>
                </div>
              </Form>
            </FormErrorHandler>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAccessLinkModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

FreeTrialLoginForm.propTypes = {
  subscriptionId: PropTypes.string.isRequired,
  initialEmail: PropTypes.string,
  autoLoginErrors: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
    field: PropTypes.string,
  })),
};

FreeTrialLoginForm.defaultProps = {
  initialEmail: '',
  autoLoginErrors: [],
};

export default FreeTrialLoginForm;
